import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { PropTypes } from 'prop-types';

import { STATUS } from '@zenbusiness/application-commons-ecommerce/useSubmitOrder';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { CHECKOUT } from '../../constants';
import { useCheckoutContext } from '../../hooks/useCheckoutContext';
import { useCheckoutSteps } from '../../hooks/useCheckoutSteps';

const { STEP, ACTION, STEP_STATUS } = CHECKOUT;

const stepTitlesMap = Object.freeze({
  [STEP.ACCOUNT]: <FormattedMessage id="shopping.page.checkout.account.title" />,
  [STEP.BUSINESS]: <FormattedMessage id="shopping.page.checkout.business.title" />,
  [STEP.CART]: <FormattedMessage id="shopping.page.checkout.cart.title" />,
  [STEP.PAYMENT]: <FormattedMessage id="shopping.page.checkout.payment.title" />
});

export const CheckoutCardHeader = ({ hideEdit, step, ...delegated }) => {
  const [{ stepStatus, placeOrder }, dispatch] = useCheckoutContext();
  const stepOrder = useCheckoutSteps();

  const isIncomplete = stepStatus[step] === STEP_STATUS.INCOMPLETE;
  const isCompleted = stepStatus[step] === STEP_STATUS.COMPLETE;

  const iconBackgroundColor = (() => {
    if (isCompleted) return 'background.green.light';
    if (isIncomplete) return 'action.disabledBackground';
    return 'common.black';
  })();

  const handleEdit = () => {
    dispatch({
      type: ACTION.GO_TO_STEP,
      payload: step,
      stepOrder
    });
  };

  const showEditButton = !hideEdit && isCompleted && placeOrder.status !== STATUS.PENDING;

  return (
    <Stack alignItems="center" component="header" direction="row" spacing={1} {...delegated}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: iconBackgroundColor,
          borderRadius: 99,
          minWidth: 28,
          minHeight: 28
        }}
      >
        {isCompleted ? (
          <CheckRoundedIcon sx={{ fontSize: 20 }} />
        ) : (
          <Text color="white" component="span" size="md" weight="bold">
            {stepOrder.indexOf(step) + 1}
          </Text>
        )}
      </Box>
      <Box flex={1}>
        <Heading size="lg" sx={{ color: isIncomplete ? 'text.disabled' : 'text.primary' }}>
          {stepTitlesMap[step]}
        </Heading>
      </Box>
      {showEditButton && (
        <Button
          clickstreamProps={{
            elementName: 'checkout_card_edit',
            elementValue: step
          }}
          color="info"
          data-testid={`checkout-card-edit-${step}`}
          size="small"
          sx={{
            padding: 0,

            '& > span': {
              margin: 0
            }
          }}
          variant="text"
          onClick={handleEdit}
        >
          <FormattedMessage id="shopping.page.checkout.edit" />
        </Button>
      )}
    </Stack>
  );
};

CheckoutCardHeader.propTypes = {
  hideEdit: PropTypes.bool,
  step: PropTypes.oneOf(Object.values(STEP)).isRequired
};
